<template>
  <DemoModal :show="isModalOpen" :url="brevoLink" @close="closeModal" />
  <section class="discover-section">
    <div class="pin-container" ref="pinContainerRef">
      <!-- 3. Usamos v-for para crear un panel por cada característica -->
      <div
        v-for="(feature, index) in features"
        :key="feature.id"
        class="discover-panel"
        :style="{ zIndex: index }"
        ref="panelRefs"
      >
        <div class="discover-content">
          <!-- Header de la sección (título y subtítulo) -->
          <div class="section-header">
            <h2 class="main-title">
              {{ feature.title }}
              <span class="highlight">{{ feature.highlight }}</span>
            </h2>
            <p class="subtitle">{{ feature.subtitle }}</p>
          </div>

          <!-- Showcase de la imagen -->
          <div class="dashboard-showcase">
            <div class="dashboard-container">
              <img :src="feature.imageUrl" :alt="feature.title" class="dashboard-image" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Contenido que aparece DESPUÉS de que termina la animación de barrido -->
    <div class="after-pin-content">
      <div class="cta-section">
        <button class="cta-button" @click="openModal">
          <span>Solicita una Demo</span>
          <!-- ... svg ... -->
        </button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import DemoModal from './DemoModal.vue'
import { useDemoModal } from '../composables/useDemoModal'
import dashboardImage from '/src/assets/images/dashboard2.png'
import activosImage from '/src/assets/images/activos1.png'
import ordenesImage from '/src/assets/images/ordenes.png'
import mantenimientosImage from '/src/assets/images/mantenimientos2.png'
import reportesImage from '/src/assets/images/reportes.png'

gsap.registerPlugin(ScrollTrigger)

const { isModalOpen, brevoLink, openModal, closeModal } = useDemoModal()

// --- 1. DATOS: Centralizamos todo el contenido aquí ---
const features = ref([
  {
    id: 'dashboard',
    title: 'Descubre todo lo que puedes hacer con ',
    highlight: 'Fourin Track',
    subtitle:
      'Software CMMS en la nube para gestión de activos, mantenimiento preventivo y predictivo con sensores inteligentes y datos en tiempo real.',
    imageUrl: dashboardImage,
  },
  {
    id: 'activos',
    title: 'Gestiona y supervisa todos tus ',
    highlight: 'Activos',
    subtitle:
      ' Registra, monitorea y controla en tiempo real la hoja de vida y el estado de cada activo.',
    imageUrl: activosImage,
  },
  {
    id: 'ordenes',
    title: 'Optimiza tus flujos de trabajo con las ',
    highlight: 'Órdenes de Trabajo',
    subtitle:
      'Crea, asigna y da seguimiento a órdenes de trabajo en un solo lugar, agiliza la comunicación con tu equipo de trabajo y ten el control de tus operaciones.',
    imageUrl: ordenesImage,
  },
  {
    id: 'mantenimientos',
    title: 'Anticípate a los problemas con los ',
    highlight: 'Mantenimientos',
    subtitle: 'Programa mantenimientos para reducir fallas y extender la vida útil de tus activos.',
    imageUrl: mantenimientosImage,
  },
  {
    id: 'reportes',
    title: 'Toma decisiones basadas en datos con tus ',
    highlight: 'Reportes',
    subtitle:
      'Genera reportes automáticos y obtén indicadores clave para tomar decisiones rápidas y acertadas. y este para el reporte.',
    imageUrl: reportesImage,
  },
])

const pinContainerRef = ref<HTMLElement | null>(null)
const panelRefs = ref<HTMLElement[]>([])

onMounted(() => {
  // Primero, verificamos el tamaño de la ventana
  if (window.innerWidth <= 768) {
    // Si estamos en móvil, le decimos a ScrollTrigger que se deshabilite por completo
    ScrollTrigger.disable()
  }

  // Después, creamos la animación como siempre.
  // En móvil, esta animación se creará pero estará "dormida", no se ejecutará.
  setTimeout(() => {
    const timeline = gsap.timeline({
      scrollTrigger: {
        trigger: pinContainerRef.value,
        pin: true,
        scrub: 1,
        start: 'top top',
        end: '+=3000',
      },
    })

    panelRefs.value.slice(1).forEach((panel) => {
      timeline.from(panel, {
        yPercent: 100,
        ease: 'none',
      })
    })
  }, 0)
})
</script>

<style scoped>
.discover-section {
  padding-bottom: 10px;
  background: white;
}

.pin-container {
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.discover-panel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 105%;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
}

.discover-content {
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}
.main-title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  max-width: 900px;
  color: #1a202c;
  margin-bottom: 20px;
  line-height: 1.2;
}
.highlight {
  color: #129bef;
  font-weight: 700;
}
.subtitle {
  font-size: 1.125rem;
  line-height: 1.6;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
}

.dashboard-showcase {
  width: 100%;
  display: flex;
  justify-content: center;
}
.dashboard-container {
  width: 100%;
  max-width: 800px;
  border-radius: 4px;
  overflow: hidden;
}
.dashboard-image {
  width: 100%;
  height: auto;
  display: block;
}

.after-pin-content {
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
}
.cta-section {
  text-align: center;
}
.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #129bef;
  color: white;
  border: none;
  padding: 18px 36px;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}
.cta-button:hover {
  transform: translateY(-3px);
}

@media (max-width: 768px) {
  .pin-container,
  .discover-panel {
    position: static;
    height: auto;
    padding: 60px 0;
  }
  .discover-section {
    padding: 40px 20px;
  }
  .main-title {
    font-size: 2rem;
  }
}
</style>
